// src/game/Stacks.js
import * as THREE from 'three';
import * as CANNON from 'cannon';
import gsap from 'gsap';
import { Text } from 'troika-three-text';
import GameMover from './GameMover.js';

class Stacks {
    config = {
        game: {
            boxSize: 0.5,
            boxHeight: 0.05,
            foundationHeight: 0.6,
            startDistance: -1.0,
            baseSpeed: 0.0008,
            speedIncrement: 0.000005,
        },
        desktop: {
            cameraFrustumSize: 2,
            cameraFollowSpeed: 0.05,
            initialPosition: { x: 2, y: 2, z: 2 },
        },
        vr: {
            startPosition: { x: 0, y: -1, z: -2.0 }
        },
        ar: {
            startPosition: { x: 0, y: -2, z: -1.5 },
            moverStartPosition: new THREE.Vector3(0, -0.65, 0),
            moverStartScale: new THREE.Vector3(0.8, 0.05, 0.8), // Scale for the game mover in AR
        },
        stars: {
            yCenter: 20,
            ySpread: 20,
        },
        ui: {
            buttonSize: 0.15,
            iconPaths: {
                play: 'assets/icons/play.png',
                restart: 'assets/icons/restart.png',
                passthroughShow: 'assets/icons/passthrough_show.png',
                passthroughHide: 'assets/icons/passthrough_hide.png',
                sfxOn: 'assets/icons/sfx_on.png',
                sfxOff: 'assets/icons/sfx_off.png',
                musicOn: 'assets/icons/music_on.png',
                musicOff: 'assets/icons/music_off.png',
            }
        }
    };

    endGameDelayMs = 500;
    camera;
    scene;
    renderer;
    world;
    lastTime = 0;
    stack = [];
    overhangs = [];
    gameEnded;
    firstStart;
    baseHue;
    blockHue;
    perfectParticleTex;
    particlesPerfect = [];

    gameContainer;
    ui2d;

    gameGroup;
    uiGroup;
    scoreText3d;
    mainActionButton;
    passthroughButton;
    sfxMuteButton;
    musicMuteButton;
    controllers = [];
    raycaster;
    starParticleSystem;
    skyGradient;
    isPassthroughActive = false;
    isSfxMuted = false;
    isMusicMuted = false;

    audioListener;
    sounds = {};
    textureLoader = new THREE.TextureLoader();

    originalBoxSize = this.config.game.boxSize;
    boxHeight = this.config.game.boxHeight;
    startDistance = this.config.game.startDistance;
    gameMover;
    stackContainer;

    constructor({ gameContainer, ui2d, leaderboard }) {
        this.gameContainer = gameContainer;
        this.leaderboard = leaderboard || null;
        // --- CHANGE: Add references to new 2D UI elements ---
        this.ui2d = {
            container: ui2d,
            score: ui2d.querySelector('#score-2d'),
            start: ui2d.querySelector('#start-btn-2d'),
            restart: ui2d.querySelector('#restart-btn-2d'),
            sfxMute: ui2d.querySelector('#sfx-mute-btn-2d'),
            musicMute: ui2d.querySelector('#music-mute-btn-2d'),
            sfxIcon: ui2d.querySelector('#sfx-icon-2d'),
            musicIcon: ui2d.querySelector('#music-icon-2d')
        };

        const pathToParticle = 'assets/ShFX_SquareHollow_Blur.png';
        this.perfectParticleTex = this.textureLoader.load(pathToParticle);

        this.init();
        window.addEventListener('resize', this.onResize);
        this.onResize();
    }

    init = () => {
        this.firstStart = true;
        this.gameEnded = true;
        this.lastTime = 0;
        this.stack = [];
        this.overhangs = [];

        this.scene = new THREE.Scene();


        const aspect = window.innerWidth / window.innerHeight;
        const frustumSize = this.config.desktop.cameraFrustumSize;
        this.camera = new THREE.OrthographicCamera(frustumSize * aspect / -2, frustumSize * aspect / 2, frustumSize / 2, frustumSize / -2, 0.1, 100);
        const { x, y, z } = this.config.desktop.initialPosition;
        this.camera.position.set(x, y, z);
        this.camera.lookAt(0, 0, 0);

        this.audioListener = new THREE.AudioListener();
        this.camera.add(this.audioListener);
        this.loadSounds();


        this.renderer = new THREE.WebGLRenderer({ antialias: true, alpha: true });
        this.renderer.setClearColor(0x000000, 0);
        this.renderer.setSize(window.innerWidth, window.innerHeight);
        this.renderer.setPixelRatio(window.devicePixelRatio);
        this.renderer.xr.enabled = true;
        this.renderer.setAnimationLoop(this.animation);
        this.gameContainer.appendChild(this.renderer.domElement);

        this.world = new CANNON.World();
        this.world.gravity.set(0, -9.8, 0);
        this.world.broadphase = new CANNON.NaiveBroadphase();
        this.world.solver.iterations = 40;

        this.gameGroup = new THREE.Group();
        this.scene.add(this.gameGroup);
        this.stackContainer = new THREE.Group();
        this.gameGroup.add(this.stackContainer);
        
        this.addStarParticles();
        this.addSkyGradient();
        this.updateSkyGradientColor();
        this.create3dUi();
        this.setupXrControls();
        this.bindDesktopControls();
        this.addFirstLayer(0, 0, this.originalBoxSize, this.originalBoxSize);

        const ambientLight = new THREE.AmbientLight(0xffffff, 0.6);
        this.scene.add(ambientLight);
        const dirLight = new THREE.DirectionalLight(0xffffff, 0.6);
        dirLight.position.set(5, 10, 7.5);
        this.scene.add(dirLight);
        this.gameMover = new GameMover({
            scene: this.scene,
            gameGroup: this.gameGroup,
            controllers: this.controllers,
            camera: this.camera,
            moveSensitivity: 5, // <-- EXAMPLE: Game will move 50% further than your hand
            moverStartPosition: this.config.ar.moverStartPosition,
            moverStartScale: this.config.ar.moverStartScale
        });


    }

    onEnterXR = () => {
        this.ui2d.container.style.display = 'none';

        const oldCamera = this.camera;
        oldCamera.remove(this.audioListener);

        this.camera = new THREE.PerspectiveCamera(75, window.innerWidth / window.innerHeight, 0.1, 100);
        this.camera.add(this.audioListener);

        this.gameMover = new GameMover({
            scene: this.scene,
            gameGroup: this.gameGroup,
            controllers: this.controllers,
            camera: this.camera,
            moveSensitivity: 5, // <-- EXAMPLE: Game will move 50% further than your hand
            moverStartPosition: this.config.ar.moverStartPosition,
            moverStartScale: this.config.ar.moverStartScale
        });

//        this.stackContainer.add(this.gameMover.grabHandle);
        const { x, y, z } = this.config.vr.startPosition;
        this.gameGroup.position.set(x, y, z);
        this.gameGroup.rotation.set(0, 0, 0);

        const session = this.renderer.xr.getSession();
        const passthroughSupported = session.environmentBlendMode === 'alpha-blend' || session.environmentBlendMode === 'additive';

        this.isPassthroughActive = false;
        this.skyGradient.visible = true;
        this.starParticleSystem.visible = true;
        this.passthroughButton.visible = passthroughSupported;
        this.sfxMuteButton.visible = true;
        this.musicMuteButton.visible = false;

        this.restart();

        this.addFirstLayer(0, 0, this.originalBoxSize, this.originalBoxSize);

        const ambientLight = new THREE.AmbientLight(0xffffff, 0.6);
        this.scene.add(ambientLight);
        const dirLight = new THREE.DirectionalLight(0xffffff, 0.6);
        dirLight.position.set(5, 10, 7.5);
        this.scene.add(dirLight);

        this.setNewColors();
        this.updateSkyGradientColor();
        if (this.gameMover) {
            this.gameMover.activate();
        }

    }

    onExitXR = () => {
        this.ui2d.container.style.display = 'flex';

        const oldCamera = this.camera;
        oldCamera.remove(this.audioListener);

        this.skyGradient.visible = true;
        this.starParticleSystem.visible = true;
        this.mainActionButton.visible = false;
        this.scoreText3d.visible = false;
        this.passthroughButton.visible = false;
        this.sfxMuteButton.visible = false;
        this.musicMuteButton.visible = false;

        const aspect = window.innerWidth / window.innerHeight;
        const frustumSize = this.config.desktop.cameraFrustumSize;
        this.camera = new THREE.OrthographicCamera(frustumSize * aspect / -2, frustumSize * aspect / 2, frustumSize / 2, frustumSize / -2, 0.1, 100);
        const { x, y, z } = this.config.desktop.initialPosition;
        this.camera.position.set(x, y, z);
        this.camera.lookAt(0, 0, 0);
        this.camera.add(this.audioListener);

        this.gameGroup.position.set(0, 0, 0);
        this.restart();

        if (this.gameMover) {
            this.gameMover.deactivate();
        }
    }



    bindDesktopControls = () => {
        this.ui2d.start.addEventListener('click', this.start);
        this.ui2d.restart.addEventListener('click', this.start);
        this.ui2d.sfxMute.addEventListener('click', this.toggleSfxMute);
        //this.ui2d.musicMute.addEventListener('click', this.toggleMusicMute);
        document.addEventListener('keydown', this.onKeyPressed);
        this.gameContainer.addEventListener('mousedown', this.onDesktopAction);
    }

    onKeyPressed = (event) => {
        if (event.key === " ") {
            event.preventDefault();
            this.onDesktopAction();
        }
    }

    onDesktopAction = () => {
        if (this.renderer.xr.isPresenting) return;
        this.placeBlock();
    }

    setupXrControls = () => {
        this.raycaster = new THREE.Raycaster();

        for (let i = 0; i < 2; i++) {
            const controller = this.renderer.xr.getController(i);
            controller.addEventListener('selectstart', () => this.handleControllerSelect(controller));
            this.scene.add(controller);

            const controllerGrip = this.renderer.xr.getControllerGrip(i);
            const model = new THREE.Mesh(new THREE.BoxGeometry(0.05, 0.05, 0.15), new THREE.MeshStandardMaterial({ color: 0x333333 }));
            controllerGrip.add(model);
            this.scene.add(controllerGrip);

            const pointerGeometry = new THREE.BufferGeometry().setFromPoints([new THREE.Vector3(0, 0, 0), new THREE.Vector3(0, 0, -1)]);
            const pointer = new THREE.Line(pointerGeometry);
            pointer.name = 'pointer';
            pointer.scale.z = 5;
            controller.add(pointer);
            this.controllers.push(controller);
        }
    }

    handleControllerSelect = (controller) => {
        const intersections = this.getIntersections(controller);

        if (intersections.length > 0) {
            const button = intersections[0].object.parent;
            this.animateButtonClick(button);

            if (button === this.passthroughButton) {
                this.togglePassthrough();
                return;
            }
            if (button === this.sfxMuteButton) {
                this.toggleSfxMute();
                return;
            }
            if (button === this.musicMuteButton) {
                this.toggleMusicMute();
                return;
            }
            if (this.gameEnded && button === this.mainActionButton) {
                this.start();
                return;
            }
        }

        if (!this.gameEnded) {
            this.placeBlock();
        }
    }

    placeBlock = () => {
        if (this.gameEnded) return;
        this.splitBlockAndAddNextOneIfOverlaps();
    }

    getIntersections = (controller) => {
        this.raycaster.setFromXRController(controller);
        return this.raycaster.intersectObjects([this.mainActionButton, this.passthroughButton, this.sfxMuteButton, this.musicMuteButton], true);
    }

    create3dUi = () => {
        this.uiGroup = new THREE.Group();
        this.gameGroup.add(this.uiGroup);

        this.scoreText3d = new Text();
        this.scoreText3d.text = 'Score: 0';
        this.scoreText3d.fontSize = 0.08;
        this.scoreText3d.color = 0xFFFFFF;
        this.scoreText3d.anchorX = 'center';
        this.scoreText3d.position.y = 0.3;
        this.scoreText3d.position.z = 0.01;
        this.scoreText3d.visible = false;
        // --- CHANGE: Add outline for readability ---
        this.scoreText3d.outlineColor = 0x000000;
        this.scoreText3d.sync();
        this.uiGroup.add(this.scoreText3d);

        this.mainActionButton = this.createUiButton(this.config.ui.iconPaths.play, 0.2);
        this.mainActionButton.position.y = -0.1;
        this.mainActionButton.visible = false;
        this.uiGroup.add(this.mainActionButton);


        this.uiGroup.position.y = 0.5;
        this.passthroughButton = this.createUiButton(this.config.ui.iconPaths.passthroughShow);
        this.passthroughButton.position.set(this.originalBoxSize / 2 + 0.15, 0.1, 0);
        this.passthroughButton.visible = false;
        this.gameGroup.add(this.passthroughButton);

        this.sfxMuteButton = this.createUiButton(this.config.ui.iconPaths.sfxOn);
        this.sfxMuteButton.position.set(this.originalBoxSize / 2 + 0.15, -0.1, 0);
        this.sfxMuteButton.visible = false;
        this.gameGroup.add(this.sfxMuteButton);

        this.musicMuteButton = this.createUiButton(this.config.ui.iconPaths.musicOn);
        this.musicMuteButton.position.set(this.originalBoxSize / 2 + 0.15, -0.2, 0);
        this.musicMuteButton.visible = false;
        this.gameGroup.add(this.musicMuteButton);
    }

    createUiButton = (iconPath, size = this.config.ui.buttonSize) => {
        const button = new THREE.Group();
        const background = new THREE.Mesh(
            new THREE.CircleGeometry(size / 2, 32),
            new THREE.MeshBasicMaterial({ color: 0x000000, transparent: true, opacity: 0.5 })
        );
        button.add(background);

        const iconMaterial = new THREE.MeshBasicMaterial({
            map: this.textureLoader.load(iconPath),
            transparent: true,
        });
        const icon = new THREE.Mesh(new THREE.PlaneGeometry(size * 0.7, size * 0.7), iconMaterial);
        icon.position.z = 0.01;
        button.add(icon);
        button.userData.iconMaterial = iconMaterial;
        return button;
    }

    animateButtonClick = (button) => {
        gsap.timeline()
            .to(button.scale, { x: 0.8, y: 0.8, z: 0.8, duration: 0.1 })
            .to(button.scale, { x: 1, y: 1, z: 1, duration: 0.1 });
    }

    togglePassthrough = () => {
        this.isPassthroughActive = !this.isPassthroughActive;
        this.skyGradient.visible = !this.isPassthroughActive;
        this.starParticleSystem.visible = !this.isPassthroughActive;
        this.passthroughButton.userData.iconMaterial.map = this.textureLoader.load(
            this.isPassthroughActive ? this.config.ui.iconPaths.passthroughHide : this.config.ui.iconPaths.passthroughShow
        );
    }

    toggleSfxMute = () => {
        this.isSfxMuted = !this.isSfxMuted;
        this.audioListener.setMasterVolume(this.isSfxMuted ? 0 : 1);

        const newIconPath = this.isSfxMuted ? this.config.ui.iconPaths.sfxOff : this.config.ui.iconPaths.sfxOn;
        this.sfxMuteButton.userData.iconMaterial.map = this.textureLoader.load(newIconPath);
        this.ui2d.sfxIcon.src = newIconPath;
    }

    toggleMusicMute = () => {
        this.isMusicMuted = !this.isMusicMuted;
        this.audioListener.setMasterVolume(this.isMusicMuted ? 0 : 1);
        const newIconPath = this.isMusicMuted ? this.config.ui.iconPaths.musicOff : this.config.ui.iconPaths.musicOn;
        this.musicMuteButton.userData.iconMaterial.map = this.textureLoader.load(newIconPath);
        this.ui2d.musicIcon.src = newIconPath;
    }

    updateUiInteractions = () => {
        if (!this.renderer.xr.isPresenting) return;
        const intersectingButtons = new Set();
        let isHoveringMover = false; // Flag for the grab handle

        for (const controller of this.controllers) {
            this.raycaster.setFromXRController(controller);

            const intersections = this.getIntersections(controller);
            if (intersections.length > 0) {
                intersectingButtons.add(intersections[0].object.parent);
            }

            // --- NEW: Check for GameMover handle intersection ---
            if (this.gameMover?.grabHandle) {
                const moverIntersections = this.raycaster.intersectObject(this.gameMover.grabHandle);
                if (moverIntersections.length > 0) {
                    isHoveringMover = true;
                }
            }
        }

        [this.mainActionButton, this.passthroughButton, this.sfxMuteButton, this.musicMuteButton].forEach(button => {
            const isHit = intersectingButtons.has(button);
            gsap.to(button.scale, { x: isHit ? 1.2 : 1, y: isHit ? 1.2 : 1, z: 1, duration: 0.2 });
        });


        if (this.gameMover) {
            this.gameMover.setHoverState(isHoveringMover);
        }

    }

    restart = () => {
        this.gameEnded = true;
        this.firstStart = true;
        this.lastTime = 0;

        this.overhangs = [];
        this.particlesPerfect.forEach(p => this.stackContainer.remove(p));
        this.particlesPerfect = [];

        const toRemoveFromScene = this.stackContainer.children.filter(child => child.cannon_body);
        toRemoveFromScene.forEach(child => this.stackContainer.remove(child));

        const lights = this.scene.children.filter(child => child.isLight);
        lights.forEach(light => this.scene.remove(light));

        while (this.world.bodies.length > 0) {
            this.world.remove(this.world.bodies[0]);
        }
        this.stack = [];

        this.updateUiPosition();

        if (this.sounds.ambient && this.sounds.ambient.isPlaying) {
            this.sounds.ambient.stop();
        }

        if (this.renderer.xr.isPresenting) {
            this.mainActionButton.visible = true;
            this.mainActionButton.userData.iconMaterial.map = this.textureLoader.load(this.config.ui.iconPaths.play);
            this.scoreText3d.visible = false;
        } else {
            this.ui2d.start.style.display = 'block';
            this.ui2d.restart.style.display = 'none';
            this.ui2d.score.style.display = 'none';
        }
    }

    start = () => {
        this.restart();
        this.gameEnded = false;
        this.firstStart = false;

        if (this.sounds.ambient && !this.sounds.ambient.isPlaying) {
            this.sounds.ambient.play();
        }

        if (this.renderer.xr.isPresenting) {
            this.mainActionButton.visible = false;
            this.scoreText3d.visible = true;
        } else {
            this.ui2d.start.style.display = 'none';
            this.ui2d.restart.style.display = 'none';
            this.ui2d.score.style.display = 'block';
        }

        const ambientLight = new THREE.AmbientLight(0xffffff, 0.6);
        this.scene.add(ambientLight);
        const dirLight = new THREE.DirectionalLight(0xffffff, 0.6);
        dirLight.position.set(5, 10, 7.5);
        this.scene.add(dirLight);

        this.setNewColors();
        this.updateSkyGradientColor();
        this.addFirstLayer(0, 0, this.originalBoxSize, this.originalBoxSize);
        this.addLayer(this.startDistance, 0, this.originalBoxSize, this.originalBoxSize, "x");
        this.updateScore(0);
    }

    splitBlockAndAddNextOneIfOverlaps = () => {
        const topLayer = this.stack[this.stack.length - 1];
        const previousLayer = this.stack[this.stack.length - 2];
        const direction = topLayer.direction;
        const size = direction === "x" ? topLayer.width : topLayer.depth;
        let delta = topLayer.threejs.position[direction] - previousLayer.threejs.position[direction];
        let overhangSize = Math.abs(delta);
        let overlap = size - overhangSize;

        if (overlap > 0) {
            if (overhangSize > 0.03) {
                this.playSound('place');
                this.cutBox(topLayer, overlap, size, delta);
                const overhangShift = (overlap / 2 + overhangSize / 2) * Math.sign(delta);
                const overhangX = direction === "x" ? topLayer.threejs.position.x + overhangShift : topLayer.threejs.position.x;
                const overhangZ = direction === "z" ? topLayer.threejs.position.z + overhangShift : topLayer.threejs.position.z;
                const overhangWidth = direction === "x" ? overhangSize : topLayer.width;
                const overhangDepth = direction === "z" ? overhangSize : topLayer.depth;
                this.addOverhang(overhangX, overhangZ, overhangWidth, overhangDepth);
            } else {
                this.playSound('perfect');
                this.generateParticleBox(previousLayer.threejs.position.x, previousLayer.threejs.position.z, topLayer.width, topLayer.depth);
                topLayer.threejs.position[direction] = previousLayer.threejs.position[direction];
                topLayer.cannonjs.position[direction] = previousLayer.cannonjs.position[direction];
            }

            const nextX = direction === "x" ? topLayer.threejs.position.x : this.startDistance;
            const nextZ = direction === "z" ? topLayer.threejs.position.z : this.startDistance;

            const newWidth = topLayer.width;
            const newDepth = topLayer.depth;
            const nextDirection = direction === "x" ? "z" : "x";

            this.addLayer(nextX, nextZ, newWidth, newDepth, nextDirection);
            this.updateScore((this.stack.length - 2) * 100);
        } else {
            this.missedTheSpot();
        }
    }

    updateScore = (score) => {
        this.ui2d.score.textContent = `Score: ${score}`;
        this.scoreText3d.text = `Score: ${score}`;
        this.scoreText3d.sync();
        //this.updateUiPosition();
    }

    updateUiPosition = () => {
        // if (!this.uiGroup) return;
        // const targetY = (this.stack.length * this.boxHeight) + 0.5;
        // this.uiGroup.position.y = targetY;
    }

    missedTheSpot = () => {
        if (this.gameEnded) return;
        const topLayer = this.stack[this.stack.length - 1];
        this.addOverhang(topLayer.threejs.position.x, topLayer.threejs.position.z, topLayer.width, topLayer.depth);
        this.world.remove(topLayer.cannonjs);
        this.stackContainer.remove(topLayer.threejs);
        this.gameEnded = true;

        // Score = number of successfully placed blocks (minus foundation)
        const finalScore = Math.max(0, this.stack.length - 2);

        setTimeout(() => {
            if (this.renderer.xr.isPresenting) {
                this.mainActionButton.visible = true;
                this.mainActionButton.userData.iconMaterial.map = this.textureLoader.load(this.config.ui.iconPaths.restart);
                this.scoreText3d.visible = false;
                this.updateUiPosition();
                // Still submit score in VR, just no overlay
                if (this.leaderboard) this.leaderboard.submit(finalScore);
            } else {
                this.ui2d.score.style.display = 'none';
                // Show leaderboard overlay instead of just restart button
                if (this.leaderboard && finalScore > 0) {
                    this.leaderboard.submitAndShow(finalScore, () => {
                        this.ui2d.restart.click();
                    });
                } else {
                    this.ui2d.restart.style.display = 'block';
                }
            }
        }, this.endGameDelayMs)
    }

    addLayer = (x, z, width, depth, direction) => {
        const y = this.boxHeight * (this.stack.length - 1);
        const layer = this.generateBox(x, y, z, width, this.boxHeight, depth, false);
        layer.direction = direction;
        this.stack.push(layer);
        this.playSound('move');
    }

    addFirstLayer = (x, z, width, depth) => {
        const height = this.config.game.foundationHeight;
        const y = -height / 2 - this.boxHeight / 2;
        const layer = this.generateBox(x, y, z, width, height, depth, false);
        this.stack.push(layer);
    }

    generateBox = (x, y, z, width, height, depth, falls) => {
        const geometry = new THREE.BoxGeometry(width, height, depth);
        const color = new THREE.Color(`hsl(${this.blockHue + this.stack.length * 4}, 100%, 50%)`);
        const material = new THREE.MeshLambertMaterial({ color });
        const mesh = new THREE.Mesh(geometry, material);
        mesh.position.set(x, y, z);
        this.stackContainer.add(mesh);

        const shape = new CANNON.Box(new CANNON.Vec3(width / 2, height / 2, depth / 2));
        const mass = falls ? 5 * (width / this.originalBoxSize) * (depth / this.originalBoxSize) : 0;
        const body = new CANNON.Body({ mass, shape });
        body.position.set(x, y, z);
        this.world.addBody(body);
        mesh.cannon_body = body;
        return { threejs: mesh, cannonjs: body, width, depth };
    }

    addOverhang(x, z, width, depth) {
        const y = this.boxHeight * (this.stack.length - 2);
        const overhang = this.generateBox(x, y, z, width, this.boxHeight, depth, true);
        this.overhangs.push(overhang);
    }

    cutBox(topLayer, overlap, size, delta) {
        const direction = topLayer.direction;
        const newWidth = direction === "x" ? overlap : topLayer.width;
        const newDepth = direction === "z" ? overlap : topLayer.depth;
        topLayer.width = newWidth;
        topLayer.depth = newDepth;
        topLayer.threejs.scale[direction] = overlap / size;
        topLayer.threejs.position[direction] -= delta / 2;
        topLayer.cannonjs.position[direction] -= delta / 2;
        const shape = new CANNON.Box(new CANNON.Vec3(newWidth / 2, this.boxHeight / 2, newDepth / 2));
        topLayer.cannonjs.shapes = [];
        topLayer.cannonjs.addShape(shape);
    }

    generateParticleBox = (x, z, width, depth) => {
        const y = this.boxHeight * (this.stack.length - 2);
        const geometry = new THREE.PlaneGeometry(width, depth);
        const material = new THREE.MeshBasicMaterial({
            alphaMap: this.perfectParticleTex,
            color: 0xffffff,
            opacity: 1,
            //emissiveMap: this.perfectParticleTex, 
            // Use white as the tint for the emissive map.
            emissive: 0xffffff,
            // Now, this will properly control the brightness of the glow.
            emissiveIntensity: 1,
            transparent: true,
            blending: THREE.NormalBlending, // Switched from AdditiveBlending
            depthWrite: false, // Good practice for transparent effects
        });
        const mesh = new THREE.Mesh(geometry, material);
        mesh.position.set(x, y, z);
        mesh.rotation.x = -Math.PI / 2;
        this.particlesPerfect.push(mesh);
        this.stackContainer.add(mesh);
    }

    animatePerfectParticles = (timePassed) => {
        let i = this.particlesPerfect.length;
        while (i--) {
            const particle = this.particlesPerfect[i];
            if (particle.material.opacity > 0) {
                particle.material.opacity -= 0.002 * timePassed;
                particle.scale.x += 0.001 * timePassed;
                particle.scale.y += 0.001 * timePassed;
            } else {
                this.stackContainer.remove(particle);
                this.particlesPerfect.splice(i, 1);
            }
        }
    }

    addStarParticles = () => {
        const vertexShader = `attribute float size; varying vec3 vColor; void main() { vColor = color; vec4 mvPosition = modelViewMatrix * vec4( position, 1.0 ); gl_PointSize = size * ( 300.0 / -mvPosition.z ); gl_Position = projectionMatrix * mvPosition; }`;
        const fragmentShader = `uniform sampler2D pointTexture; varying vec3 vColor; void main() { gl_FragColor = vec4( vColor, 1.0 ); gl_FragColor = gl_FragColor * texture2D( pointTexture, gl_PointCoord ); }`;
        const uniforms = { pointTexture: { value: this.textureLoader.load('assets/ShFX_Circle_Glow.png') } };
        const shaderMaterial = new THREE.ShaderMaterial({ uniforms, vertexShader, fragmentShader, blending: THREE.AdditiveBlending, depthTest: true, depthWrite: false, transparent: true, vertexColors: true });
        const starGeometry = new THREE.BufferGeometry();
        const starCount = 1000;
        const radius = 100;
        const positions = [];
        const colors = [];
        const sizes = [];
        const color = new THREE.Color();
        for (let i = 0; i < starCount; i++) {
            positions.push(
                (Math.random() - 0.5) * radius,
                this.config.stars.yCenter + (Math.random() - 0.5) * this.config.stars.ySpread,
                (Math.random() - 0.5) * radius
            );
            color.setHSL(i / starCount, 0.7, 0.8);
            colors.push(color.r, color.g, color.b);
            sizes.push(2);
        }
        starGeometry.setAttribute('position', new THREE.Float32BufferAttribute(positions, 3));
        starGeometry.setAttribute('color', new THREE.Float32BufferAttribute(colors, 3));
        starGeometry.setAttribute('size', new THREE.Float32BufferAttribute(sizes, 1).setUsage(THREE.DynamicDrawUsage));
        this.starParticleSystem = new THREE.Points(starGeometry, shaderMaterial);
        this.starParticleSystem.raycast = () => { };
        this.scene.add(this.starParticleSystem);
    }

    animateStars = (time) => {
        if (!this.starParticleSystem) return;
        const sizes = this.starParticleSystem.geometry.attributes.size.array;
        const timeFactor = time * 0.0005;
        for (let i = 0; i < sizes.length; i++) {
            sizes[i] = 0.1 * (1 + Math.sin(0.1 * i + timeFactor));
        }
        this.starParticleSystem.geometry.attributes.size.needsUpdate = true;
    }

    addSkyGradient = () => {
        const vertexShader = `varying vec3 vWorldPosition; void main() { vWorldPosition = position; gl_Position = projectionMatrix * modelViewMatrix * vec4(position, 1.0); gl_Position.z = gl_Position.w; }`;
        const fragmentShader = `uniform vec3 topColor; uniform vec3 bottomColor; varying vec3 vWorldPosition; void main() { float h = normalize(vWorldPosition).y; float t = (h + 1.3) / 2.0; gl_FragColor = vec4(mix(bottomColor, topColor, t), 1.0); }`;
        const uniforms = { topColor: { value: new THREE.Color(0x000000) }, bottomColor: { value: new THREE.Color(0x3d0066) } };
        const material = new THREE.ShaderMaterial({ uniforms, vertexShader, fragmentShader, side: THREE.BackSide, depthWrite: false });
        const geometry = new THREE.BoxGeometry(20, 20, 20);
        const mesh = new THREE.Mesh(geometry, material);
        mesh.position.set(0, -5, 0);
        mesh.renderOrder = -99999;
        this.skyGradient = mesh;
        this.scene.add(mesh);
    }

    updateSkyGradientColor = () => {
        this.skyGradient.material.uniforms.bottomColor.value.setHSL(this.baseHue / 360, 0.7, 0.5);
    }

    loadSounds = () => {
        const loader = new THREE.AudioLoader();
        const soundNames = ['move', 'place', 'perfect'];
        soundNames.forEach(name => {
            loader.load(`assets/sounds/${name}.mp3`, (buffer) => {
                this.sounds[name] = new THREE.Audio(this.audioListener);
                this.sounds[name].setBuffer(buffer);
            });
        });

        loader.load(`assets/sounds/ambient.mp3`, (buffer) => {
            this.sounds.ambient = new THREE.Audio(this.audioListener);
            this.sounds.ambient.setBuffer(buffer);
            this.sounds.ambient.setLoop(true);
            this.sounds.ambient.setVolume(0.3);
        });
    }

    playSound = (name) => {
        if (this.isSfxMuted) return;
        if (this.sounds[name]) {
            if (this.sounds[name].isPlaying) {
                this.sounds[name].stop();
            }
            this.sounds[name].play();
        }
    }

    animation = (time, frame) => {
        const timePassed = this.lastTime > 0 ? time - this.lastTime : 0;
        this.updateUiInteractions();
        this.animatePerfectParticles(timePassed);
        this.animateStars(time);

        if (!this.gameEnded && !this.firstStart) {
            const speed = this.config.game.baseSpeed + (this.stack.length * this.config.game.speedIncrement);
            const topLayer = this.stack[this.stack.length - 1];
            const newPos = topLayer.threejs.position[topLayer.direction] + speed * timePassed;

            topLayer.threejs.position[topLayer.direction] = newPos;
            topLayer.cannonjs.position[topLayer.direction] = newPos;

            if (Math.abs(newPos) > Math.abs(this.startDistance) + 0.5) {
                this.missedTheSpot();
            }

            if (this.renderer.xr.isPresenting) {
                const targetY =  -((this.stack.length - 2) * this.boxHeight);
                this.stackContainer.position.y += (targetY - this.stackContainer.position.y) * this.config.desktop.cameraFollowSpeed;
            } else {
                const targetY = this.boxHeight * this.stack.length + 2;
                this.camera.position.y += (targetY - this.camera.position.y) * this.config.desktop.cameraFollowSpeed;
                //this.camera.lookAt(0, this.boxHeight * (this.stack.length - 1), 0);
            }
        }

        this.world.step(1 / 60, timePassed / 1000, 3);

        this.stackContainer.children.forEach(child => {
            if (child.cannon_body) {
                child.position.copy(child.cannon_body.position);
                child.quaternion.copy(child.cannon_body.quaternion);
            }
        });

        if (this.gameMover) {
            this.gameMover.update();
        }
        this.lastTime = time;
        this.renderer.render(this.scene, this.camera);
    }

    onResize = () => {
        console.log('Resizing renderer');
        if (!this.renderer.xr.isPresenting) {
            const { clientWidth, clientHeight } = this.gameContainer;
            const aspect = clientWidth / clientHeight;
            const frustumSize = this.config.desktop.cameraFrustumSize;
            this.camera.left = frustumSize * aspect / -2;
            this.camera.right = frustumSize * aspect / 2;
            this.camera.top = frustumSize / 2;
            this.camera.bottom = frustumSize / -2;
            this.camera.updateProjectionMatrix();
            this.renderer.setSize(clientWidth, clientHeight);
        }
    }

    setNewColors = () => {
        this.baseHue = Math.random() * 360;
        this.blockHue = (this.baseHue + 180) % 360;
    }

    dispose = () => {
        this.renderer.setAnimationLoop(null);
        window.removeEventListener('resize', this.onResize);
        document.removeEventListener('keydown', this.onKeyPressed);
        this.gameContainer.removeEventListener('mousedown', this.onDesktopAction);

        Object.values(this.sounds).forEach((sound) => {
            if (!sound) return;
            if (sound.isPlaying) sound.stop();
            if (typeof sound.disconnect === 'function') sound.disconnect();
        });
        this.sounds = {};

        // Dispose of Three.js objects
        this.scene.traverse(object => {
            if (object.isMesh) {
                if (object.geometry) {
                    object.geometry.dispose();
                }
                if (object.material) {
                    if (Array.isArray(object.material)) {
                        object.material.forEach(material => material.dispose());
                    } else {
                        object.material.dispose();
                    }
                }
            }
        });
        this.renderer.dispose();
    }
}

export default Stacks;